import { useLocalStorage } from './useLocalStorage';

export type AlertCategory = 'liquidity' | 'crypto' | 'signal';

export interface AlertRule {
  id: string;
  label: string;
  category: AlertCategory;
  /** indicator id or signal domain */
  target: string;
  /** threshold interpretation depends on target */
  threshold: number;
  unit: string;
  enabled: boolean;
  /** pro-only rules are locked for free users */
  pro: boolean;
}

const defaultRules: AlertRule[] = [
  {
    id: 'liq-score-change',
    label: 'Global Liquidity score changes by',
    category: 'signal',
    target: 'liquidity_score',
    threshold: 10,
    unit: 'pts',
    enabled: true,
    pro: false,
  },
  {
    id: 'bch-setup-change',
    label: 'BCH Setup score changes by',
    category: 'signal',
    target: 'bch_score',
    threshold: 10,
    unit: 'pts',
    enabled: true,
    pro: false,
  },
  {
    id: 'cycle-change',
    label: 'Liquidity Cycle phase changes',
    category: 'signal',
    target: 'cycle',
    threshold: 1,
    unit: 'phase',
    enabled: false,
    pro: true,
  },
  {
    id: 'usdjpy',
    label: 'USD/JPY moves by',
    category: 'liquidity',
    target: 'usdjpy',
    threshold: 2,
    unit: '%',
    enabled: false,
    pro: false,
  },
  {
    id: 'dxy',
    label: 'DXY moves by',
    category: 'liquidity',
    target: 'dxy',
    threshold: 1.5,
    unit: '%',
    enabled: false,
    pro: true,
  },
  {
    id: 'fima',
    label: 'FIMA repo usage changes by',
    category: 'liquidity',
    target: 'fima_repo',
    threshold: 20,
    unit: '%',
    enabled: false,
    pro: true,
  },
  {
    id: 'hormuz',
    label: 'Strait of Hormuz risk moves by',
    category: 'liquidity',
    target: 'hormuz_risk',
    threshold: 10,
    unit: 'pts',
    enabled: false,
    pro: true,
  },
  {
    id: 'btc',
    label: 'BTC trend moves by',
    category: 'crypto',
    target: 'btc_trend',
    threshold: 8,
    unit: '%',
    enabled: false,
    pro: false,
  },
  {
    id: 'bchbtc',
    label: 'BCH/BTC rotation event',
    category: 'crypto',
    target: 'bch_btc',
    threshold: 5,
    unit: '%',
    enabled: false,
    pro: true,
  },
  {
    id: 'btc-dom',
    label: 'BTC dominance moves by',
    category: 'crypto',
    target: 'btc_dominance',
    threshold: 2,
    unit: '%',
    enabled: false,
    pro: true,
  },
];

export function useAlerts() {
  const [rules, setRules] = useLocalStorage<AlertRule[]>(
    'bchliq:alerts',
    defaultRules,
  );

  const toggle = (id: string) => {
    setRules(rules.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r)));
  };

  const setThreshold = (id: string, threshold: number) => {
    setRules(rules.map((r) => (r.id === id ? { ...r, threshold } : r)));
  };

  return { rules, toggle, setThreshold };
}
